import React, { FC, useState, useEffect } from 'react';
import { useTranslation } from '@packages/utils';
import {
  EEventPartType,
  EEventStatus,
  TPartsUI,
} from 'data/types/events.types';
import dayjs from 'dayjs';
import {
  useEditPartDateMutation,
  useEditPartTimeMutation,
} from 'redux/apiSlice/events.slice';
import {
  ENotificationType,
  useShowToastNotification,
} from 'components/Base/Notification';
import { TRtkErrorType } from 'data/types/generic.types';
import { Flex } from 'antd';
import { CalendarOutline } from 'assets/icon/icon';
import { Text } from 'components/Base/Typography';
import CalendarDatePicker from 'components/Shared/CalendarDatePicker/CalendarDatePicker';
import {
  EventDatePickerWrapper,
  EventTimePicker,
} from './PartDateInput.styles';

const partNumberMap = {
  [EEventPartType.PART_ONE]: '1',
  [EEventPartType.PART_TWO]: '2',
  [EEventPartType.PART_THREE]: '3',
};

const PartDateInput: FC<{
  part: TPartsUI;
  eventId: string;
  nextPartDateTime?: string;
  previousPartDateTime?: string;
  eventStatus?: EEventStatus;
}> = ({
  part,
  eventId,
  previousPartDateTime,
  nextPartDateTime,
  eventStatus,
}) => {
  const { t } = useTranslation({
    keyPrefix:
      'descriptions.admin-portal.events.details.sessions.view.actions.update.date',
  });
  const { t: tDateTime } = useTranslation({
    keyPrefix: 'descriptions.admin-portal.events.details.basic-info.parts-date',
  });
  const [editPartDateTime] = useEditPartDateMutation();
  const [editPartTime] = useEditPartTimeMutation(); // Add this mutation

  const [localStartTime, setLocalStartTime] = useState<string | null>(
    part.startTime || null,
  );
  const [localEndTime, setLocalEndTime] = useState<string | null>(
    part.endTime || null,
  );

  useEffect(() => {
    setLocalStartTime(part.startTime || null);
  }, [part.startTime]);

  useEffect(() => {
    setLocalEndTime(part.endTime || null);
  }, [part.endTime]);

  const needToDisable = eventStatus === EEventStatus.PUBLISHED;
  const { showToastNotification } = useShowToastNotification();

  const shouldDisableDatePicker = () => {
    if (part.date?.allowedOperations.readonly) return true;
    if (!needToDisable) return false;

    // If we have a current date value, check if it would be disabled
    if (part.date?.value) {
      const currentDate = dayjs(part.date.value);
      const nextPart = dayjs(nextPartDateTime);
      const prevPart = dayjs(previousPartDateTime);
      const dateNow = dayjs();

      const disableDaysBeforeNow = currentDate.isBefore(dateNow);
      const disableAfterNextPart =
        currentDate.isAfter(nextPart) || currentDate.isSame(nextPart);
      const disableBeforePrevPart =
        currentDate.isBefore(prevPart) || currentDate.isSame(prevPart);

      if (!previousPartDateTime && nextPartDateTime) {
        return disableAfterNextPart || disableDaysBeforeNow;
      }
      if (previousPartDateTime && !nextPartDateTime) {
        return disableBeforePrevPart || disableDaysBeforeNow;
      }
      if (previousPartDateTime && nextPartDateTime) {
        return (
          disableBeforePrevPart || disableAfterNextPart || disableDaysBeforeNow
        );
      }
      return disableDaysBeforeNow;
    }

    return needToDisable;
  };

  // Base rule for the date picker (existing logic)
  const isDatePickerDisabled = shouldDisableDatePicker();

  // New enabling/disabling logic for time pickers based on selected values
  const hasDateSelected = Boolean(part.date?.value);
  const hasStartTimeSelected = Boolean(localStartTime);

  // Global rule: if the date picker is disabled due to status/readonly, time pickers should also be disabled.
  // Additionally, time pickers are disabled when there is no date selected.
  const isStartTimeDisabled =
    isDatePickerDisabled || !hasDateSelected || needToDisable; // keep original publish rule

  // End time requires both date and start time to be selected
  const isEndTimeDisabled =
    isDatePickerDisabled ||
    !hasDateSelected ||
    !hasStartTimeSelected ||
    needToDisable; // keep original publish rule

  const label =
    part.partType !== EEventPartType.STANDALONE
      ? `Day ${partNumberMap[part.partType]}`
      : `${tDateTime('LABEL')}`;

  const onEditDate = async (value: string | null) => {
    if (!value) return;

    try {
      await editPartDateTime({
        dateTime: value,
        eventId,
        eventPartId: part.eventPartId,
      }).unwrap();
      showToastNotification({
        message: t('SUCCESS_MESSAGE'),
        type: ENotificationType.SUCCESS,
      });
    } catch (e) {
      showToastNotification({
        message: (e as TRtkErrorType)?.data?.message,
        type: ENotificationType.ERROR,
      });
    }
  };

  const onEditTime = async (
    timeType: 'START_TIME' | 'END_TIME',
    value: string | null,
  ) => {
    if (!value) return;

    if (timeType === 'START_TIME') {
      setLocalStartTime(value);
    } else {
      setLocalEndTime(value);
    }

    try {
      await editPartTime({
        time: value,
        timeType,
        eventId,
        eventPartId: part.eventPartId,
      }).unwrap();
      showToastNotification({
        message: t('SUCCESS_MESSAGE'),
        type: ENotificationType.SUCCESS,
      });
    } catch (e) {
      // Revert local state on error
      if (timeType === 'START_TIME') {
        setLocalStartTime(part.startTime || null);
      } else {
        setLocalEndTime(part.endTime || null);
      }
      showToastNotification({
        message: (e as TRtkErrorType)?.data?.message,
        type: ENotificationType.ERROR,
      });
    }
  };

  return (
    <Flex align="center" data-cy="event-date-input" gap={10}>
      <Flex align="center" gap={8} style={{ width: 160 }}>
        <CalendarOutline size={24} />
        <Text color="#808080">{label}</Text>
      </Flex>

      <EventDatePickerWrapper>
        <CalendarDatePicker
          value={
            part.date?.value
              ? dayjs(part.date.value).format('YYYY-MM-DD')
              : undefined
          }
          buttonLabel="Select Date"
          disabled={isDatePickerDisabled}
          placeholder="Select Date"
          onDateChange={onEditDate}
          format="YYYY-MM-DD"
        />
      </EventDatePickerWrapper>

      <Flex align="center" gap={8}>
        <Text color="#808080">From</Text>
        <EventTimePicker
          value={localStartTime ? dayjs(localStartTime, 'HH:mm') : undefined}
          disabled={isStartTimeDisabled}
          placeholder="Select Time"
          format="h:mm A"
          minuteStep={15}
          use12Hours
          showNow={false}
          hideDisabledOptions
          onChange={time =>
            onEditTime('START_TIME', time ? time.format('HH:mm:ss') : null)
          }
        />
      </Flex>

      <Flex align="center" gap={8}>
        <Text color="#808080">To</Text>
        <EventTimePicker
          value={localEndTime ? dayjs(localEndTime, 'HH:mm') : undefined}
          disabled={isEndTimeDisabled}
          placeholder="Select Time"
          format="h:mm A"
          minuteStep={15}
          use12Hours
          showNow={false}
          hideDisabledOptions
          onChange={time =>
            onEditTime('END_TIME', time ? time.format('HH:mm:ss') : null)
          }
        />
      </Flex>
    </Flex>
  );
};

export default PartDateInput;
